export const EMPLOYEE_BADGE_ONBOARDING_STORAGE_KEY = "employee-badge-onboarding"

export const EMPLOYEE_BADGE_ONBOARDING_STATUSES = {
  /** Intro has not been actioned yet; it keeps offering on every refresh. */
  unstarted: "unstarted",
  /** Tour was started; each screen's chapter plays once as the user roams. */
  active: "active",
  /** Skipped, finished, or a badge was paired — nothing shows again. */
  completed: "completed",
} as const

export type EmployeeBadgeOnboardingStatus =
  (typeof EMPLOYEE_BADGE_ONBOARDING_STATUSES)[keyof typeof EMPLOYEE_BADGE_ONBOARDING_STATUSES]

export const EMPLOYEE_BADGE_TOUR_CHAPTERS = {
  hub: "hub",
  scan: "scan",
  available: "available",
  assign: "assign",
} as const

export type EmployeeBadgeTourChapter =
  (typeof EMPLOYEE_BADGE_TOUR_CHAPTERS)[keyof typeof EMPLOYEE_BADGE_TOUR_CHAPTERS]

type EmployeeBadgeOnboardingState = {
  status: EmployeeBadgeOnboardingStatus
  /** Chapters that already played, so each screen spotlights only once. */
  seen: EmployeeBadgeTourChapter[]
}

const DEFAULT_STATE: EmployeeBadgeOnboardingState = {
  status: EMPLOYEE_BADGE_ONBOARDING_STATUSES.unstarted,
  seen: [],
}

const isStatus = (value: unknown): value is EmployeeBadgeOnboardingStatus =>
  typeof value === "string" &&
  Object.values<string>(EMPLOYEE_BADGE_ONBOARDING_STATUSES).includes(value)

const isChapter = (value: unknown): value is EmployeeBadgeTourChapter =>
  typeof value === "string" &&
  Object.values<string>(EMPLOYEE_BADGE_TOUR_CHAPTERS).includes(value)

export const readEmployeeBadgeOnboardingState =
  (): EmployeeBadgeOnboardingState => {
    const stored = window.localStorage.getItem(
      EMPLOYEE_BADGE_ONBOARDING_STORAGE_KEY,
    )

    if (!stored) {
      return DEFAULT_STATE
    }

    try {
      const parsed: unknown = JSON.parse(stored)

      if (typeof parsed !== "object" || parsed === null) {
        return DEFAULT_STATE
      }

      const { status, seen } = parsed as { status: unknown; seen: unknown }

      return {
        status: isStatus(status)
          ? status
          : EMPLOYEE_BADGE_ONBOARDING_STATUSES.unstarted,
        seen: Array.isArray(seen) ? seen.filter(isChapter) : [],
      }
    } catch {
      return DEFAULT_STATE
    }
  }

const writeEmployeeBadgeOnboardingState = (
  state: EmployeeBadgeOnboardingState,
) => {
  window.localStorage.setItem(
    EMPLOYEE_BADGE_ONBOARDING_STORAGE_KEY,
    JSON.stringify(state),
  )
}

export const setEmployeeBadgeOnboardingStatus = (
  status: EmployeeBadgeOnboardingStatus,
) => {
  writeEmployeeBadgeOnboardingState({
    ...readEmployeeBadgeOnboardingState(),
    status,
  })
}

export const markEmployeeBadgeChapterSeen = (
  chapter: EmployeeBadgeTourChapter,
) => {
  const state = readEmployeeBadgeOnboardingState()

  if (state.seen.includes(chapter)) {
    return
  }

  writeEmployeeBadgeOnboardingState({
    ...state,
    seen: [...state.seen, chapter],
  })
}

/**
 * Badges can only be added from the mobile app, so the first paired badge proves
 * the customer got there without the tour. Called from the add flow and from the
 * hub when it loads a non-empty badge count, so a device that never ran the tour
 * does not start offering it after the fact.
 */
export const completeEmployeeBadgeOnboarding = () => {
  if (
    readEmployeeBadgeOnboardingState().status ===
    EMPLOYEE_BADGE_ONBOARDING_STATUSES.completed
  ) {
    return
  }

  setEmployeeBadgeOnboardingStatus(EMPLOYEE_BADGE_ONBOARDING_STATUSES.completed)
}
